/**
 * Shared helpers for the "open viewing schedule" field on rent/buy listings
 * (client/src/components/ListPropertyPage.js for input, PropertyDetailsPage.js
 * for display) - kept in one place so the two pages can't drift on how a
 * schedule slot is normalized or summarized.
 */

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
export const WEEKENDS = ['Saturday', 'Sunday'];

// Every schedule shape this field has ever been saved in:
// 1. { day: 'Monday', startTime, endTime } - original, one day per slot
// 2. { days: ['Monday', 'Wednesday'], startTime, endTime } - multi-day, no mode
// 3. { mode: 'recurring' | 'onetime', days, date, startTime, endTime } - current
//
// `days` and `date` are both always carried through regardless of the
// active mode, so switching mode back and forth in the form doesn't
// silently drop whatever was picked for the other mode.
export function normalizeSlot(slot) {
  const days = slot.days || (slot.day ? [slot.day] : []);
  return {
    mode: slot.mode === 'onetime' ? 'onetime' : 'recurring',
    days,
    date: slot.date || '',
    startTime: slot.startTime || '',
    endTime: slot.endTime || '',
  };
}

function sameDaySet(a, b) {
  return a.length === b.length && b.every((day) => a.includes(day));
}

// Which preset (if any) the current day selection matches, for highlighting
// the preset button - not stored, derived fresh from `days` every render so
// it can never drift out of sync with the actual selection.
export function getPresetForDays(days) {
  if (sameDaySet(days, DAYS_OF_WEEK)) {return 'everyday';}
  if (sameDaySet(days, WEEKDAYS)) {return 'weekdays';}
  if (sameDaySet(days, WEEKENDS)) {return 'weekends';}
  return null;
}

export function formatTime12h(hhmm) {
  if (!hhmm) {return '';}
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

export function formatOneTimeDate(isoDate) {
  if (!isoDate) {return '';}
  // Parsed as local, not UTC, so the date shown always matches what was
  // picked regardless of the viewer's timezone offset from UTC midnight.
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function summarizeSlot(rawSlot) {
  const slot = normalizeSlot(rawSlot);
  const timeRange =
    slot.startTime && slot.endTime
      ? `${formatTime12h(slot.startTime)} – ${formatTime12h(slot.endTime)}`
      : '';

  let when;
  if (slot.mode === 'onetime') {
    when = formatOneTimeDate(slot.date);
  } else {
    const preset = getPresetForDays(slot.days);
    if (preset === 'everyday') {when = 'Every day';}
    else if (preset === 'weekdays') {when = 'Weekdays';}
    else if (preset === 'weekends') {when = 'Weekends';}
    else {when = slot.days.map((d) => d.slice(0, 3)).join(', ');}
  }

  if (!when && !timeRange) {return '';}
  if (!when) {return timeRange;}
  if (!timeRange) {return when;}
  return `${when} · ${timeRange}`;
}
